/**
 * Position Budget (rework spine) — see docs/ROLE_MASTER_BUDGET_REWORK.md
 *
 * The budget is per POSITION (the seat we fund), costed by its WORKER (the
 * incumbent's Sage actual). This view operationalises the three guarantees:
 *
 *   1. Every active position is budgeted   → Seed from F&O + Cost Vacancies +
 *      the coverage cards (filled / vacant / costed / uncosted).
 *   2. Workers are correctly allocated     → Allocation integrity panel
 *      (multi-position workers, orphans, filled-but-inactive, line review).
 *   3. Stale positions are retired         → the retirement queue.
 *
 * RF1 is demoted to an optional overlay (upload at the bottom) — it layers a
 * Finance plan onto the F&O-anchored lines; it never creates a seat.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, RefreshCw, DatabaseZap, Calculator, Users, AlertTriangle,
  CheckCircle, FileUp, Layers, ChevronDown, ChevronRight, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  budgetService,
  BudgetReconciliation,
  Rf1OverlayResult,
} from '@/services/api/organizationService';

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtZar(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount || 0);
}
function fmtNum(n: number): string {
  return new Intl.NumberFormat('en-ZA').format(n || 0);
}

type Msg = { type: 'success' | 'error' | 'info'; text: string } | null;

const REVIEW_LABELS: Record<string, { label: string; tone: string }> = {
  ok: { label: 'OK', tone: 'text-emerald-600 border-emerald-200' },
  unbudgeted: { label: 'Uncosted', tone: 'text-amber-600 border-amber-200' },
  occupancy_change: { label: 'Occupancy changed', tone: 'text-blue-600 border-blue-200' },
  stale: { label: 'Retired', tone: 'text-gray-500 border-gray-200' },
};

// Costing-basis provenance — how each costed seat got its amount.
const BASIS_META: Record<string, { label: string; tone: string }> = {
  sage_actual: { label: 'Salaried (Sage actual)', tone: 'text-emerald-700' },
  sage_earnings: { label: 'Sessional / variable (payslip actuals)', tone: 'text-teal-700' },
  band_median: { label: 'Vacant (band median)', tone: 'text-amber-700' },
  rf1: { label: 'RF1 plan', tone: 'text-blue-700' },
  manual: { label: 'Manual', tone: 'text-gray-700' },
  unknown: { label: 'Other', tone: 'text-gray-500' },
};
const BASIS_ORDER = ['sage_actual', 'sage_earnings', 'band_median', 'rf1', 'manual', 'unknown'];

// ── Coverage cards (envelope + costing coverage) ──────────────────────────

const CoverageCards: React.FC<{ recon: BudgetReconciliation }> = ({ recon }) => {
  const s = recon.summary;
  const coverage = s.lines > 0 ? Math.round((s.costed / s.lines) * 100) : 0;
  const basisEntries = BASIS_ORDER
    .filter((b) => s.by_basis?.[b])
    .map((b) => ({ key: b, meta: BASIS_META[b] || { label: b, tone: 'text-gray-500' }, ...s.by_basis![b] }));
  return (
    <div className="space-y-3">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <Card>
        <CardContent className="p-3">
          <div className="text-2xl font-bold text-heritage-blue">{fmtNum(s.lines)}</div>
          <div className="text-xs text-gray-500" title="Establishment spine: one budget line per live F&O position for this year — retired seats are excluded.">Budget lines (seats) — live establishment</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-2xl font-bold text-blue-600">{fmtNum(s.filled)}</div>
          <div className="text-xs text-gray-500">Filled</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-2xl font-bold text-amber-600">{fmtNum(s.vacant)}</div>
          <div className="text-xs text-gray-500">Vacant</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-2xl font-bold text-emerald-600">{fmtNum(s.costed)}</div>
          <div className="text-xs text-gray-500">Costed ({coverage}%)</div>
        </CardContent>
      </Card>
      <Card className={cn(s.uncosted > 0 && 'ring-1 ring-amber-200')}>
        <CardContent className="p-3">
          <div className={cn('text-2xl font-bold', s.uncosted > 0 ? 'text-amber-600' : 'text-gray-400')}>
            {fmtNum(s.uncosted)}
          </div>
          <div className="text-xs text-gray-500">Uncosted</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-lg font-bold text-emerald-700">{fmtZar(s.annual_seed_total)}</div>
          <div className="text-xs text-gray-500">Seed envelope (annual)</div>
          {s.annual_budgeted_total > 0 && (
            <div className="text-[10px] text-gray-400 mt-0.5">RF1 plan {fmtZar(s.annual_budgeted_total)}</div>
          )}
        </CardContent>
      </Card>
    </div>
    {basisEntries.length > 0 && (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 text-xs">
        <span className="font-medium text-gray-400">Costed basis</span>
        {basisEntries.map((b) => (
          <span key={b.key} className="flex items-center gap-1.5">
            <span className={cn('font-semibold', b.meta.tone)}>{fmtNum(b.lines)}</span>
            <span className="text-gray-500">{b.meta.label}</span>
            <span className="text-gray-400">{fmtZar(b.envelope)}</span>
          </span>
        ))}
      </div>
    )}
    </div>
  );
};

// ── Allocation integrity ──────────────────────────────────────────────────

const AllocationPanel: React.FC<{ recon: BudgetReconciliation }> = ({ recon }) => {
  const a = recon.allocation;
  const [showMulti, setShowMulti] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);
  const orphanSample = a.orphan_workers_sample || [];
  const states = a.line_review_states || {};
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Users className="h-4 w-4" /> Worker Allocation Integrity
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => setShowMulti(v => !v)}
            className={cn(
              'rounded-md border p-3 text-left transition-colors',
              a.workers_multi_position.count > 0 ? 'hover:bg-amber-50/50 ring-1 ring-amber-200' : 'opacity-70',
            )}
          >
            <div className={cn('text-xl font-bold', a.workers_multi_position.count > 0 ? 'text-amber-600' : 'text-gray-400')}>
              {fmtNum(a.workers_multi_position.count)}
            </div>
            <div className="text-xs text-gray-500">On &gt;1 position</div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              {a.workers_multi_position.count > 0 ? 'Click to inspect' : 'Each worker holds one seat'}
            </div>
          </button>
          <button
            onClick={() => setShowOrphans(v => !v)}
            className={cn(
              'rounded-md border p-3 text-left transition-colors',
              a.orphan_workers > 0 ? 'hover:bg-red-50/50 ring-1 ring-red-200' : 'opacity-70',
            )}
          >
            <div className={cn('text-xl font-bold', a.orphan_workers > 0 ? 'text-red-500' : 'text-gray-400')}>
              {fmtNum(a.orphan_workers)}
            </div>
            <div className="text-xs text-gray-500">Unallocated staff</div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              {a.orphan_workers > 0 ? 'Active, no seat — click to review' : 'Active, no seat'}
            </div>
          </button>
          <div className={cn('rounded-md border p-3', a.filled_with_inactive_incumbent > 0 && 'ring-1 ring-red-200')}>
            <div className={cn('text-xl font-bold', a.filled_with_inactive_incumbent > 0 ? 'text-red-500' : 'text-gray-400')}>
              {fmtNum(a.filled_with_inactive_incumbent)}
            </div>
            <div className="text-xs text-gray-500">Filled by inactive</div>
            <div className="text-[10px] text-gray-400 mt-0.5">Should have vacated</div>
          </div>
          <div className="rounded-md border p-3">
            <div className="flex flex-wrap gap-1 mt-0.5">
              {Object.keys(states).length === 0 ? (
                <span className="text-gray-400 text-sm">—</span>
              ) : (
                Object.entries(states).map(([k, n]) => {
                  const meta = REVIEW_LABELS[k] || { label: k, tone: 'text-gray-500 border-gray-200' };
                  return (
                    <Badge key={k} variant="outline" className={cn('text-[10px]', meta.tone)}>
                      {meta.label}: {fmtNum(n)}
                    </Badge>
                  );
                })
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">Line review states</div>
          </div>
        </div>

        {showMulti && a.workers_multi_position.sample.length > 0 && (
          <div className="border rounded max-h-[260px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-[11px] bg-gray-50">
                  <TableHead>PN</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Positions held</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {a.workers_multi_position.sample.map((w, i) => (
                  <TableRow key={`${w.pn}-${i}`} className="text-xs">
                    <TableCell className="font-mono text-gray-500">{w.pn || '—'}</TableCell>
                    <TableCell className="font-medium">{w.name || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="text-amber-600 border-amber-200">{w.positions}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {a.workers_multi_position.count > a.workers_multi_position.sample.length && (
              <div className="text-[11px] text-gray-400 text-center py-1.5 border-t">
                Showing {a.workers_multi_position.sample.length} of {a.workers_multi_position.count}
              </div>
            )}
          </div>
        )}

        {showOrphans && orphanSample.length > 0 && (
          <div className="border rounded max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-[11px] bg-gray-50">
                  <TableHead>PN</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Job title</TableHead>
                  <TableHead>Employment</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphanSample.map((w, i) => (
                  <TableRow key={`${w.pn}-${i}`} className="text-xs">
                    <TableCell className="font-mono text-gray-500">{w.pn || '—'}</TableCell>
                    <TableCell className="font-medium">{w.name || '—'}</TableCell>
                    <TableCell className="text-gray-600">{w.title || '—'}</TableCell>
                    <TableCell>
                      {w.employment
                        ? <Badge variant="outline" className="text-[10px] text-gray-500">{w.employment}</Badge>
                        : <span className="text-gray-300">—</span>}
                    </TableCell>
                    <TableCell className="text-gray-400">{w.email || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {a.orphan_workers > orphanSample.length && (
              <div className="text-[11px] text-gray-400 text-center py-1.5 border-t">
                Showing {orphanSample.length} of {fmtNum(a.orphan_workers)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ── RF1 overlay upload ─────────────────────────────────────────────────────

const Rf1Overlay: React.FC<{ year: number; onApplied: () => void }> = ({ year, onApplied }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Rf1OverlayResult | null>(null);
  const [msg, setMsg] = useState<Msg>(null);
  const [open, setOpen] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setMsg(null);
    setResult(null);
    try {
      const res = await budgetService.rf1Overlay(file, year);
      setResult(res);
      setMsg({ type: 'success', text: `Overlaid ${fmtNum(res.matched_pn)} PN rows onto position lines for ${year}.` });
      onApplied();
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.detail || err?.message || 'RF1 overlay failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => setOpen(v => !v)}>
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Layers className="h-4 w-4" /> RF1 Finance Overlay (optional)
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">
            Layer a Finance RF1 worksheet onto the F&O-anchored lines for <span className="font-medium">{year}</span>:
            PN rows write the planned amount onto the matching position line; Rev*/Vacancy*/New* rows become forward demand;
            Savings/Transfer/Removal/Special become envelope adjustments. It never creates a seat. Seed first. Idempotent per year.
          </p>
          <label className="cursor-pointer inline-block">
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" disabled={busy} />
            <div className={cn(
              'inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50 transition-colors',
              busy && 'opacity-50 cursor-not-allowed',
            )}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {busy ? 'Applying overlay…' : 'Upload RF1 worksheet'}
            </div>
          </label>

          {msg && (
            <div className={cn(
              'rounded p-2.5 text-xs flex items-center gap-2',
              msg.type === 'success' ? 'bg-emerald-50 border border-emerald-100 text-emerald-700'
                : 'bg-red-50 border border-red-100 text-red-700',
            )}>
              {msg.type === 'success' ? <CheckCircle className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
              {msg.text}
            </div>
          )}

          {result && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="rounded border p-2">
                <div className="font-semibold text-emerald-700">{fmtNum(result.matched_pn)}</div>
                <div className="text-gray-500">Lines overlaid · {fmtZar(result.overlaid_total)}</div>
              </div>
              <div className="rounded border p-2">
                <div className="font-semibold text-blue-700">{fmtNum(result.demands)}</div>
                <div className="text-gray-500">Demands · {fmtZar(result.demand_total)}</div>
              </div>
              <div className="rounded border p-2">
                <div className="font-semibold text-purple-700">{fmtNum(result.adjustments)}</div>
                <div className="text-gray-500">Adjustments · {fmtZar(result.adjustment_total)}</div>
              </div>
              <div className="rounded border p-2">
                <div className="font-semibold text-amber-600">{fmtNum(result.unmatched_pn)}</div>
                <div className="text-gray-500">Unmatched PNs · {fmtNum(result.nil)} nil</div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ── Main view ──────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();

const PositionBudgetView: React.FC = () => {
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [recon, setRecon] = useState<BudgetReconciliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [withActuals, setWithActuals] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [costing, setCosting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  const loadRecon = useCallback(async () => {
    setLoading(true);
    try {
      setRecon(await budgetService.getReconciliation(year));
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Failed to load reconciliation' });
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { loadRecon(); }, [loadRecon]);

  const handleSeed = async () => {
    setSeeding(true);
    setMsg(null);
    try {
      const res = await budgetService.seedFromFo(year, withActuals);
      setMsg({
        type: 'success',
        text: `Seeded ${fmtNum(res.positions)} positions → ${fmtNum(res.created)} new / ${fmtNum(res.updated)} updated `
          + `(${fmtNum(res.filled)} filled, ${fmtNum(res.vacant)} vacant). `
          + (withActuals
            ? `${fmtNum(res.costed)} costed from ${fmtNum(res.salary_resolved)} Sage actuals, ${fmtNum(res.unbudgeted)} uncosted.`
            : `${fmtNum(res.unbudgeted)} uncosted (structural only).`)
          + (res.occupancy_changes > 0 ? ` ${fmtNum(res.occupancy_changes)} occupancy changes flagged.` : ''),
      });
      loadRecon();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Seed failed' });
    } finally {
      setSeeding(false);
    }
  };

  const handleCostVacancies = async () => {
    setCosting(true);
    setMsg(null);
    try {
      const res = await budgetService.costVacancies(year);
      setMsg({
        type: res.costed > 0 ? 'success' : 'info',
        text: res.bands === 0
          ? 'No grade-band medians yet — cost filled seats first (Seed with Sage actuals).'
          : `Costed ${fmtNum(res.costed)} vacant seats at the median of ${fmtNum(res.bands)} grade bands `
            + `(${fmtNum(res.no_band_median)} had no band median).`,
      });
      loadRecon();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Cost vacancies failed' });
    } finally {
      setCosting(false);
    }
  };

  const handleExportReconciliation = async () => {
    setExporting(true);
    setMsg(null);
    try {
      const blob = await budgetService.exportReconciliation(year);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Budget_Reconciliation_${year}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Export failed' });
    } finally {
      setExporting(false);
    }
  };

  const busy = seeding || costing || exporting;

  return (
    <div className="space-y-4">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Fiscal year:</span>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[90px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch id="with-actuals" checked={withActuals} onCheckedChange={setWithActuals} disabled={busy} />
            <Label htmlFor="with-actuals" className="text-xs text-gray-500 cursor-pointer">Include Sage actuals</Label>
          </div>
          <Button size="sm" onClick={handleSeed} disabled={busy}>
            {seeding ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <DatabaseZap className="h-3.5 w-3.5 mr-1" />}
            {seeding ? 'Seeding…' : 'Seed from F&O'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCostVacancies} disabled={busy}>
            {costing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Calculator className="h-3.5 w-3.5 mr-1" />}
            Cost vacancies
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportReconciliation} disabled={busy}>
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
            Export reconciliation
          </Button>
          <Button variant="ghost" size="sm" onClick={loadRecon} disabled={loading || busy}>
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {msg && (
        <div className={cn(
          'rounded p-3 text-sm flex items-start gap-2',
          msg.type === 'success' ? 'bg-emerald-50 border border-emerald-100 text-emerald-700'
            : msg.type === 'error' ? 'bg-red-50 border border-red-100 text-red-700'
              : 'bg-blue-50 border border-blue-100 text-blue-700',
        )}>
          {msg.type === 'error' ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> : <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />}
          <span>{msg.text}</span>
          <button className="ml-auto text-xs underline opacity-60 hover:opacity-100" onClick={() => setMsg(null)}>dismiss</button>
        </div>
      )}

      {loading && !recon ? (
        <div className="py-12 text-center text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading position budget…
        </div>
      ) : recon && recon.summary.lines === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <DatabaseZap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-500">No budget lines for {year} yet</p>
          <p className="text-sm mt-1">
            Seed one budget line per active F&O position (filled or vacant) to establish the spine.
          </p>
          <Button size="sm" className="mt-3" onClick={handleSeed} disabled={busy}>
            {seeding ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <DatabaseZap className="h-3.5 w-3.5 mr-1" />}
            Seed from F&O
          </Button>
        </div>
      ) : recon ? (
        <>
          <CoverageCards recon={recon} />
          <AllocationPanel recon={recon} />
          <Rf1Overlay year={year} onApplied={loadRecon} />
        </>
      ) : null}
    </div>
  );
};

export default PositionBudgetView;
